import { Controller } from "@hotwired/stimulus"
import { createRrgChart, createRrgSlider, updateRrgChart, CHART_BLOCK_HEIGHT_RATIO } from "../charts/d3/rrg_chart"
import { loadBundle } from "../utils/lazy_bundle"

// Constants
const MS_PER_DAY = 1000 * 60 * 60 * 24
const QUADRANT_THRESHOLD = 100

// Fold-fitting (see #fitToFold). The plot is square, so its width is bounded by
// the height available below it — sizing by width alone pushes the bottom half
// off a laptop screen. Below MIN_FIT_VIEWPORT (phones) the column width is the
// real constraint and scrolling is expected, so the fit is skipped; below
// MIN_CHART_WIDTH the chart would be too small to read, so it is allowed to
// overflow the fold instead.
const MIN_FIT_VIEWPORT = 768
const MIN_CHART_WIDTH = 260
const FOLD_BOTTOM_ALLOWANCE = 24
const FOLD_FIT_PASSES = 4

const QUADRANT_COLORS = {
  leading: "#90EE90",   // Light green
  weakening: "#FFD700", // Gold
  lagging: "#FFA07A",   // Light salmon
  improving: "#87CEFA"  // Light sky blue
}

/**
 * Determine which quadrant a point belongs in based on x,y coordinates.
 * @param {number} x - RS-Ratio value
 * @param {number} y - RS-Momentum value
 * @returns {string} Quadrant name
 */
function determineQuadrant(x, y) {
  if (x >= QUADRANT_THRESHOLD && y >= QUADRANT_THRESHOLD) return "leading"
  if (x >= QUADRANT_THRESHOLD && y < QUADRANT_THRESHOLD) return "weakening"
  if (x < QUADRANT_THRESHOLD && y < QUADRANT_THRESHOLD) return "lagging"
  return "improving"
}

/**
 * Get the color for a given quadrant.
 * @param {string} quadrant - Quadrant name
 * @returns {string} Hex color code
 */
function getColorForQuadrant(quadrant) {
  return QUADRANT_COLORS[quadrant] || "#999999"
}

/**
 * Create a point object from a sector historical data point.
 * @param {Object} sectorPoint - Historical data point
 * @returns {Object} Formatted point object
 */
function createPointFromHistorical(sectorPoint) {
  return {
    id: sectorPoint.id,
    x: sectorPoint.x,
    y: sectorPoint.y,
    date: sectorPoint.dateString,
    quadrant: sectorPoint.quadrant,
    color: sectorPoint.color
  }
}

/**
 * Calculate dot positions for a given endDay (right edge of slider selection).
 * The dot position is determined ONLY by endDay, not startDay.
 * This ensures dots move as the slider's right edge moves.
 *
 * @param {Object} data - The processed chart data with historical points and days array
 * @param {number} endDay - The day offset for the right edge of the slider
 * @returns {Array} Array of current dot positions
 */
export function calculateDotPositions(data, endDay) {
  const currentData = []
  const sectorIds = [...new Set(data.historical.map(d => d.id))]

  // Find the two nearest trading days that bracket endDay for interpolation
  const sortedDays = [...data.days].sort((a, b) => a - b)
  const daysBeforeOrAt = sortedDays.filter(d => d <= endDay)
  const daysAfter = sortedDays.filter(d => d > endDay)

  const dayBefore = daysBeforeOrAt.length > 0 ? daysBeforeOrAt[daysBeforeOrAt.length - 1] : null
  const dayAfter = daysAfter.length > 0 ? daysAfter[0] : null

  sectorIds.forEach(id => {
    let point = null

    if (dayBefore !== null && dayAfter !== null && dayBefore !== dayAfter) {
      // Interpolate between the two nearest data points
      const pointBefore = data.historical.find(d => d.id === id && d.day === dayBefore)
      const pointAfter = data.historical.find(d => d.id === id && d.day === dayAfter)

      if (pointBefore && pointAfter) {
        // Linear interpolation factor: how far are we between dayBefore and dayAfter?
        const t = (endDay - dayBefore) / (dayAfter - dayBefore)
        const interpX = pointBefore.x + t * (pointAfter.x - pointBefore.x)
        const interpY = pointBefore.y + t * (pointAfter.y - pointBefore.y)
        const quadrant = determineQuadrant(interpX, interpY)

        point = {
          id: id,
          x: interpX,
          y: interpY,
          date: `Interpolated (${pointBefore.dateString} - ${pointAfter.dateString})`,
          quadrant: quadrant,
          color: getColorForQuadrant(quadrant)
        }
      }
    }

    // If no interpolation possible, use nearest available point
    if (!point && dayBefore !== null) {
      const sectorPoint = data.historical.find(d => d.id === id && d.day === dayBefore)
      if (sectorPoint) {
        point = createPointFromHistorical(sectorPoint)
      }
    }

    // If endDay is before all historical data, use the earliest available day
    if (!point && dayBefore === null && dayAfter !== null) {
      const sectorPoint = data.historical.find(d => d.id === id && d.day === dayAfter)
      if (sectorPoint) {
        point = createPointFromHistorical(sectorPoint)
      }
    }

    if (point) {
      currentData.push(point)
    } else {
      // No historical data at all for this sector, use the current point
      const current = data.current.find(d => d.id === id)
      if (current) currentData.push(current)
    }
  })

  return currentData
}

/**
 * One decimal place, rounding halves up — matching Ruby's
 * number_with_precision, which renders the same numbers server-side. toFixed
 * breaks ties the other way often enough to make a row flicker by one tick the
 * moment the slider takes over the table.
 *
 * @param {number} value
 * @returns {string}
 */
function oneDecimal(value) {
  return (Math.round(value * 10 + Number.EPSILON) / 10).toFixed(1)
}

/**
 * The most recent day in `days` that is at or before endDay — i.e. the trading
 * day the dots actually represent when the brush edge lands on a weekend or a
 * holiday. Returns null when endDay precedes every day in the series.
 *
 * @param {Array<number>} days - Day offsets present in the data
 * @param {number} endDay - Day offset of the brush's right edge
 * @returns {number|null} The effective day offset
 */
export function effectiveDay(days, endDay) {
  let best = null
  days.forEach(day => {
    if (day <= endDay && (best === null || day > best)) best = day
  })
  return best
}

export default class extends Controller {
  static targets = ["container", "row", "asAt"]
  static values = {
    chartData: Object,
    endDate: String,
    tailLength: Number,
    quadrantOrder: Array
  }

  async connect() {
    if (!this.hasContainerTarget) return

    // d3 lives in its own on-demand bundle (see utils/lazy_bundle.js);
    // initChart() below reports the missing-library case itself.
    try {
      await loadBundle("d3")
    } catch (error) {
      console.error(error)
    }
    if (!this.element.isConnected) return

    // Using requestAnimationFrame ensures the DOM is fully ready and browser is idle
    window.requestAnimationFrame(() => {
      this.initChart()
      this.fitToFold()
      this.watchForResize()
    })
  }

  /**
   * Size the chart from the space it has, not from the width of the page.
   *
   * The plot is square and the slider sits above it, so the whole block is a
   * fixed multiple of its own width — sized by width alone, a wide window puts
   * the bottom half of the chart below the fold. This inverts that: measure
   * what is left of the scroll viewport below the chart's top edge, and cap the
   * width so the block ends there.
   *
   * Measurements are taken against the scrolling container rather than the
   * window: its height already excludes the navbar and the footer bar, and the
   * distance is scroll-independent, so a resize part-way down the page doesn't
   * compute a chart the height of the whole viewport.
   */
  fitToFold() {
    if (!this.hasContainerTarget) return

    const element = this.containerTarget
    element.style.maxWidth = ""
    if (window.innerWidth < MIN_FIT_VIEWPORT) return

    const scroller = this.element.closest(".main-content") || document.documentElement
    const scrollerRect = scroller.getBoundingClientRect()
    const distance = element.getBoundingClientRect().top - scrollerRect.top + scroller.scrollTop
    const budget = scroller.clientHeight - distance - FOLD_BOTTOM_ALLOWANCE
    if (budget <= 0) return

    // Correct against the rendered height rather than modelling it: the two
    // SVGs scale with the width but the flex gap between them doesn't, and
    // rounding is real. Iterating converges without hardcoding the extras.
    // (The caption is outside this element, so it is already counted in
    // `distance`.)
    // Growth is capped at the column width — past that a bigger max-width
    // changes nothing, and the loop would chase an overflow it can't close.
    const fullWidth = element.parentElement.getBoundingClientRect().width
    let width = element.getBoundingClientRect().width

    for (let pass = 0; pass < FOLD_FIT_PASSES; pass++) {
      const overflow = element.getBoundingClientRect().height - budget
      if (Math.abs(overflow) < 2) break

      const next = Math.min(fullWidth, Math.max(MIN_CHART_WIDTH, width - overflow / CHART_BLOCK_HEIGHT_RATIO))
      if (Math.round(next) === Math.round(width)) break

      width = next
      element.style.maxWidth = `${Math.round(width)}px`
    }
  }

  watchForResize() {
    // Observing the scroller catches window resizes, zoom changes and anything
    // that changes the space available (a dismissed announcement banner, say)
    // in one place.
    const scroller = this.element.closest(".main-content") || document.documentElement
    this.resizeObserver = new ResizeObserver(() => this.fitToFold())
    this.resizeObserver.observe(scroller)
  }

  initChart() {
    // Clear out any existing chart
    this.containerTarget.innerHTML = ""

    // Check if D3 is available
    if (!window.d3) {
      console.error("D3.js is not available. Make sure it's properly loaded.")
      this.containerTarget.innerHTML = "<div class='alert alert-danger'>D3.js library is not available. Please check the console for details.</div>"
      return
    }

    // Parse end date from the value (format: YYYY-MM-DD)
    this.endDate = this.hasEndDateValue ? new Date(this.endDateValue + "T00:00:00") : new Date()
    this.tailLength = this.hasTailLengthValue ? this.tailLengthValue : 10

    // Process the data from the controller
    const processedData = this.processChartData(this.chartDataValue)

    // Create container with flex layout
    const container = window.d3.select(this.containerTarget)
      .attr("class", "rrg-container")
      .style("display", "flex")
      .style("flex-direction", "column")
      .style("gap", "8px")

    // Create elements for the chart - but don't add them yet
    const chartContainer = window.d3.create("div")
      .attr("class", "chart-container")

    // Create the RRG chart with the slider component (now slider will come first in the DOM)
    this.setupRrgWithSlider(container, chartContainer, processedData)
  }
  
  processChartData(rawData) {
    // Ensure rawData has the expected structure
    if (!rawData || !rawData.points || !Array.isArray(rawData.points)) {
      console.error("Invalid chart data structure:", rawData)
      return { current: [], historical: [], days: [], benchmark: [], endDate: this.endDate }
    }

    // Extract all symbols (for sectors) from rawData
    const sectors = {}

    // Separate points from tails
    const currentPoints = rawData.points.filter(p => !p.name.endsWith('_tail'))
    const tails = rawData.points.filter(p => p.name.endsWith('_tail'))

    // Process current points
    currentPoints.forEach(point => {
      const symbol = point.name
      const data = point.data[0]

      // Ensure we get numeric values for x and y
      const xValue = Array.isArray(data) ? parseFloat(data[0]) : 100;
      const yValue = Array.isArray(data) ? parseFloat(data[1]) : 100;

      sectors[symbol] = {
        id: symbol,
        x: xValue,
        y: yValue,
        color: point.color,
        quadrant: point.quadrant || this.determineQuadrant(xValue, yValue)
      }
    })

    // Process historical data from tails
    const historical = []
    const endDate = this.endDate

    tails.forEach(tail => {
      const symbol = tail.name.replace('_tail', '')
      if (!sectors[symbol]) return

      // Create timeline points for each position in the tail
      tail.data.forEach((point, index) => {
        const xValue = parseFloat(point.x)
        const yValue = parseFloat(point.y)
        const pointDate = point.date ? new Date(point.date + "T00:00:00") : null
        const quadrant = point.quadrant || this.determineQuadrant(xValue, yValue)
        const color = point.color || this.getColorForQuadrant(quadrant)

        // Calculate day offset from end date (negative = days in the past)
        let dayOffset = -index
        if (pointDate && endDate) {
          dayOffset = Math.round((pointDate - endDate) / MS_PER_DAY)
        }

        historical.push({
          id: symbol,
          day: dayOffset,
          x: xValue,
          y: yValue,
          date: pointDate,
          dateString: pointDate ? pointDate.toLocaleDateString() : `Day ${dayOffset}`,
          quadrant: quadrant,
          color: color
        })
      })
    })

    // Extract all available days for slider
    const days = [...new Set(historical.map(d => d.day))].sort((a, b) => a - b)

    // Process benchmark data for the slider if available.
    // Parse dates as local midnight ("T00:00:00") to match the tail dates and
    // endDate above; bare date strings would parse as UTC and skew day offsets.
    let benchmarkData = []
    if (rawData.benchmark_data && Array.isArray(rawData.benchmark_data)) {
      benchmarkData = rawData.benchmark_data.map(point => ({
        value: parseFloat(point.value),
        price: parseFloat(point.price),
        date: new Date(point.date + "T00:00:00")
      }))
    }

    return {
      current: Object.values(sectors),
      historical: historical,
      days: days,
      benchmark: benchmarkData,
      endDate: endDate,
      tailLength: this.tailLength
    }
  }

  // Delegate to module-level functions for consistency
  getColorForQuadrant(quadrant) {
    return getColorForQuadrant(quadrant)
  }

  determineQuadrant(x, y) {
    return determineQuadrant(x, y)
  }

  setupRrgWithSlider(container, chartContainer, data) {
    // Create a function to filter data for the selected time range (day-based)
    const getDataForDayRange = (startDay, endDay) => {
      // Tail: show all historical points within the selected range
      const filteredHistorical = data.historical.filter(
        d => d.day >= startDay && d.day <= endDay
      )

      // Dot position: determined by endDay (right edge of slider), not startDay
      const currentData = calculateDotPositions(data, endDay)

      return {
        current: currentData.length > 0 ? currentData : data.current,
        historical: filteredHistorical,
        days: data.days
      }
    }

    // Track the chart SVG for updates (instead of recreating)
    let chartSvg = null

    // Create a function to update the chart based on time selection (day-based)
    const updateChart = (startDay, endDay) => {
      // Get filtered data for the selected time range
      const filteredData = getDataForDayRange(startDay, endDay)

      if (!chartSvg) {
        // First call: create the chart
        chartSvg = createRrgChart(filteredData)
        chartContainer.node().appendChild(chartSvg)
        // The slider defers this first render, so until now the block was the
        // slider alone — only here is there a full-height block to fit.
        this.fitToFold()
      } else {
        // Subsequent calls: update with smooth transitions
        updateRrgChart(chartSvg, filteredData, 150)
      }

      // The table states the same positions as the dots, so it moves with them
      this.updatePositions(filteredData.current, effectiveDay(data.days, endDay))
    }

    // Create the slider (appears on top of chart)
    createRrgSlider(container.node(), data, updateChart)

    // Then append the chart container below it
    container.node().appendChild(chartContainer.node())
  }

  /**
   * Rewrite the sector table (and every "as at" label) to match the day the
   * dots are showing. Server-rendered markup is the starting state; from the
   * first brush event on, this owns it.
   *
   * @param {Array} points - Current dot positions ({id, x, y, quadrant})
   * @param {number|null} day - Effective day offset, for the date labels
   */
  updatePositions(points, day) {
    this.updateAsAtLabels(day)
    if (!this.hasRowTarget) return

    const byId = new Map(points.map(point => [point.id, point]))

    this.rowTargets.forEach(row => {
      const point = byId.get(row.dataset.symbol)
      if (!point) return

      this.setCell(row, "rs-ratio", oneDecimal(point.x))
      this.setCell(row, "rs-momentum", oneDecimal(point.y))

      const badge = row.querySelector("[data-cell='quadrant']")
      if (badge) {
        badge.textContent = point.quadrant.charAt(0).toUpperCase() + point.quadrant.slice(1)
        badge.style.backgroundColor = getColorForQuadrant(point.quadrant)
      }
      row.dataset.quadrant = point.quadrant
    })

    this.sortRows()
  }

  setCell(row, name, value) {
    const cell = row.querySelector(`[data-cell='${name}']`)
    if (cell) cell.textContent = value
  }

  /**
   * Re-order rows strongest quadrant first, matching the server's initial
   * ordering. The order itself comes from the server (RrgController::
   * QUADRANT_PRIORITY) so the two can't drift.
   */
  sortRows() {
    const order = this.quadrantOrderValue
    if (!order.length) return

    const rank = row => {
      const index = order.indexOf(row.dataset.quadrant)
      return index === -1 ? order.length : index
    }

    const tbody = this.rowTargets[0].parentNode
    const sorted = [...this.rowTargets].sort((a, b) =>
      rank(a) - rank(b) || a.dataset.symbol.localeCompare(b.dataset.symbol)
    )
    sorted.forEach(row => tbody.appendChild(row))
  }

  updateAsAtLabels(day) {
    if (day === null || day === undefined || !this.hasAsAtTarget) return

    const date = new Date(this.endDate.getTime() + day * MS_PER_DAY)
    // en-US to match the server's `l(date, format: :long)` rendering, so the
    // label doesn't change shape the first time the slider moves.
    const formatted = date.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
    this.asAtTargets.forEach(target => { target.textContent = formatted })
  }

  disconnect() {
    this.resizeObserver?.disconnect()
    this.resizeObserver = null

    // Clean up any tooltips or other elements when controller disconnects
    if (window.d3) {
      window.d3.selectAll(".rrg-tooltip").remove()
    }
  }
}